import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpRequest
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class InterceptorService {
  private readonly authEndpointPaths = [
    '/xflkauth/api/v1/flink/auth/login',
    '/xflkauth/api/v1/flink/auth/refresh'
  ];

  constructor(
    private auth: AuthService,
    private router: Router
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isApiRequest = request.url.startsWith(environment.REST_API_SERVER);
    const isAuthRequest = this.authEndpointPaths.some(path => request.url.includes(path));

    if (!isApiRequest || isAuthRequest) {
      return next.handle(request);
    }

    const token = this.auth.getAccessToken();
    if (token && this.auth.tokenExpired(token) && this.auth.canRefreshToken()) {
      return this.auth.refreshAccessToken().pipe(
        switchMap(newToken => next.handle(this.addBearerToken(request, newToken))),
        catchError(refreshError => {
          this.endSession();
          return throwError(() => refreshError);
        })
      );
    }

    const authenticatedRequest = this.addBearerToken(request, token);
    return next.handle(authenticatedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401 || !this.auth.canRefreshToken()) {
          if (error.status === 401) {
            this.endSession();
          }
          return throwError(() => error);
        }

        return this.auth.refreshAccessToken().pipe(
          switchMap(token => next.handle(this.addBearerToken(request, token))),
          catchError(refreshError => {
            this.endSession();
            return throwError(() => refreshError);
          })
        );
      })
    );
  }

  private addBearerToken(
    request: HttpRequest<unknown>,
    token: string | null
  ): HttpRequest<unknown> {
    if (!token) {
      return request;
    }

    return request.clone({
      setHeaders: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
  }

  private endSession(): void {
    this.auth.clearSession();
    void this.router.navigate(['/login']);
  }
}
